import { useState, useMemo } from "react";
import { format, isToday, isTomorrow, isYesterday, startOfDay, endOfDay, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Filter,
  ChevronLeft,
  ChevronRight,
  Search,
  MessageCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const BookingsListView = ({ bookings, onRefresh, isAdmin = false, businessId = null, business = null }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all"); // all, confirmed, pending, completed, no_show
  const [searchQuery, setSearchQuery] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);

  const token = localStorage.getItem("token");

  // Parse datetime safely
  const parseDateTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      const cleanDate = dateStr.replace(/Z$/, '');
      return new Date(cleanDate);
    } catch {
      return null;
    }
  };

  // Filter bookings by selected date and status
  const filteredBookings = useMemo(() => {
    const dayStart = startOfDay(selectedDate);
    const dayEnd = endOfDay(selectedDate);

    return bookings
      .filter(booking => {
        const bookingDate = parseDateTime(booking.start_datetime);
        if (!bookingDate) return false;
        
        // Date filter
        const isInDateRange = bookingDate >= dayStart && bookingDate <= dayEnd;
        
        // Status filter
        const statusMatch = statusFilter === "all" || 
          booking.status === statusFilter || 
          booking.attendance_status === statusFilter;
        
        // Search filter
        const searchMatch = !searchQuery || 
          booking.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          booking.customer_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          booking.customer_phone?.includes(searchQuery);
        
        return isInDateRange && statusMatch && searchMatch;
      })
      .sort((a, b) => {
        const dateA = parseDateTime(a.start_datetime);
        const dateB = parseDateTime(b.start_datetime);
        return dateA - dateB;
      });
  }, [bookings, selectedDate, statusFilter, searchQuery]);

  // Navigate dates
  const goToPrevDay = () => setSelectedDate(d => new Date(d.getTime() - 86400000));
  const goToNextDay = () => setSelectedDate(d => new Date(d.getTime() + 86400000));
  const goToToday = () => setSelectedDate(new Date());

  // Update attendance status
  const updateAttendance = async (bookingId, status) => {
    try {
      const endpoint = isAdmin 
        ? `${API}/admin/businesses/${businessId}/bookings/${bookingId}/attendance`
        : `${API}/my-business/bookings/${bookingId}/attendance`;
      
      await axios.put(endpoint, 
        { attendance_status: status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(status === "showed" ? "Presenza confermata" : "Segnato come assente");
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Get date label
  const getDateLabel = () => {
    if (isToday(selectedDate)) return "Oggi";
    if (isTomorrow(selectedDate)) return "Domani";
    if (isYesterday(selectedDate)) return "Ieri";
    return format(selectedDate, "EEEE d MMMM", { locale: it });
  };

  // Get attendance badge
  const getAttendanceBadge = (booking) => {
    if (booking.attendance_status === "showed") {
      return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Presente</Badge>;
    }
    if (booking.attendance_status === "no_show" || booking.status === "no_show") {
      return <Badge className="bg-red-100 text-red-700 text-[10px]"><XCircle className="h-3 w-3 mr-1" />Assente</Badge>;
    }
    return null;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { label: "Confermato", className: "bg-blue-100 text-blue-700" },
      pending: { label: "In attesa", className: "bg-amber-100 text-amber-700" },
      completed: { label: "Completato", className: "bg-emerald-100 text-emerald-700" },
      cancelled: { label: "Annullato", className: "bg-slate-100 text-slate-700" },
      no_show: { label: "Non presentato", className: "bg-red-100 text-red-700" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${config.className} text-[10px]`}>{config.label}</Badge>;
  };

  // Stats for selected day
  const dayStats = useMemo(() => {
    const total = filteredBookings.length;
    const showed = filteredBookings.filter(b => b.attendance_status === "showed").length;
    const noShow = filteredBookings.filter(b => b.attendance_status === "no_show" || b.status === "no_show").length;
    const pending = total - showed - noShow;
    return { total, showed, noShow, pending };
  }, [filteredBookings]);

  // Build WhatsApp link with template variables
  const buildWhatsAppLink = (booking) => {
    if (!booking.customer_phone) return null;
    
    const template = business?.whatsapp_template || "Ciao {nome_cliente}, ti ricordiamo il tuo appuntamento per {servizio} il {data} alle {ora}. A presto! {nome_attivita}";
    
    const startDt = parseDateTime(booking.start_datetime);
    const serviceNames = booking.services_details?.map(s => s.name).join(", ") || 
                         booking.services?.map(s => s.name).join(", ") || "";
    
    const message = template
      .replace(/{nome_cliente}/g, booking.customer_name || "")
      .replace(/{servizio}/g, serviceNames)
      .replace(/{data}/g, startDt ? format(startDt, "d MMMM yyyy", { locale: it }) : "")
      .replace(/{ora}/g, startDt ? format(startDt, "HH:mm") : "")
      .replace(/{nome_attivita}/g, business?.name || "");
    
    // Clean phone: remove spaces, dashes, keep + prefix
    let phone = booking.customer_phone.replace(/[\s\-()]/g, "");
    if (!phone.startsWith("+")) {
      phone = "+39" + phone; // Default to Italy
    }
    phone = phone.replace("+", "");
    
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="space-y-4">
      {/* Header with date navigation */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPrevDay} className="h-8 w-8">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[180px] justify-start text-left font-normal h-8">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {getDateLabel()}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => { setSelectedDate(date || new Date()); setCalendarOpen(false); }}
                locale={it}
              />
            </PopoverContent>
          </Popover>
          
          <Button variant="outline" size="icon" onClick={goToNextDay} className="h-8 w-8">
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          {!isToday(selectedDate) && (
            <Button variant="ghost" size="sm" onClick={goToToday} className="h-8 text-xs">
              Oggi
            </Button>
          )}
        </div>

        {/* Search and filters */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-48">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input 
              placeholder="Cerca cliente..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-8 text-sm"
            />
          </div>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-8 px-2 text-sm border rounded-md bg-white"
          >
            <option value="all">Tutti</option>
            <option value="confirmed">Confermati</option>
            <option value="pending">In attesa</option>
            <option value="completed">Completati</option>
            <option value="no_show">Assenti</option>
          </select>
        </div>
      </div>

      {/* Day stats */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-slate-50 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-slate-900">{dayStats.total}</p>
          <p className="text-[10px] text-slate-500">Totale</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-emerald-600">{dayStats.showed}</p>
          <p className="text-[10px] text-emerald-600">Presenti</p>
        </div>
        <div className="bg-red-50 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-red-600">{dayStats.noShow}</p>
          <p className="text-[10px] text-red-600">Assenti</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-amber-600">{dayStats.pending}</p>
          <p className="text-[10px] text-amber-600">Da verificare</p>
        </div>
      </div>

      {/* Bookings list */}
      <div className="space-y-2">
        {filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <CalendarIcon className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Nessuna prenotazione per questa data</p>
            </CardContent>
          </Card>
        ) : (
          filteredBookings.map((booking) => {
            const startTime = parseDateTime(booking.start_datetime);
            const endTime = parseDateTime(booking.end_datetime);
            const isPast = startTime && startTime < new Date();
            const canMarkAttendance = isPast && booking.status !== "cancelled" && !booking.attendance_status;
            
            return (
              <Card key={booking.booking_id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {/* Time column */}
                    <div className="text-center min-w-[50px]">
                      <p className="text-sm font-bold text-slate-900">
                        {startTime && format(startTime, "HH:mm")}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {endTime && format(endTime, "HH:mm")}
                      </p>
                    </div>
                    
                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium text-sm truncate">{booking.customer_name}</p>
                            {!isPast && booking.customer_phone && booking.status !== "cancelled" && (
                              <a
                                href={buildWhatsAppLink(booking)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0 p-0.5 rounded-full hover:bg-green-100 transition-colors"
                                title="Invia messaggio WhatsApp"
                                data-testid={`whatsapp-btn-${booking.booking_id}`}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4 text-green-600" fill="currentColor">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                            {booking.customer_phone && (
                              <span className="flex items-center gap-0.5">
                                <Phone className="h-3 w-3" />{booking.customer_phone}
                              </span>
                            )}
                            {booking.customer_email && (
                              <span className="flex items-center gap-0.5 truncate">
                                <Mail className="h-3 w-3" />{booking.customer_email}
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                            {booking.services_details?.map(s => s.name).join(", ") || 
                             booking.services?.map(s => s.name).join(", ")}
                          </p>
                        </div>
                        
                        {/* Status badges */}
                        <div className="flex flex-col items-end gap-1">
                          {getAttendanceBadge(booking) || getStatusBadge(booking.status)}
                          {booking.total_price > 0 && (
                            <span className="text-xs font-semibold text-emerald-600">
                              €{booking.total_price?.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Attendance buttons */}
                      {canMarkAttendance && (
                        <div className="flex gap-2 mt-2 pt-2 border-t">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1 h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => updateAttendance(booking.booking_id, "showed")}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Presente
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1 h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => updateAttendance(booking.booking_id, "no_show")}
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Assente
                          </Button>
                        </div>
                      )}
                      
                      {/* Already marked */}
                      {booking.attendance_status && (
                        <div className="mt-2 pt-2 border-t">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-6 text-[10px] text-slate-400"
                            onClick={() => updateAttendance(booking.booking_id, "pending")}
                          >
                            Reimposta stato
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BookingsListView;
